import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Eye, Trash2, ShieldCheck } from "lucide-react";
import { API, api, errMsg, fmtDate, fmtDateTime, money, statusClass, statusLabel, verifyClass, CASE_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PayFeeButton } from "@/components/PayFeeButton";
import { SignedPdf } from "@/components/SignedPdf";
import { Section, Row, btn } from "@/components/CaseBits";
import { InvitePanel } from "@/components/InvitePanel";
import { PlanBanner } from "@/components/PlanBanner";
import { VerifyPanel, TagForm, DocForm, InvoiceForm }
  from "@/components/CaseForms";
import { LimitationPanel } from "@/components/LimitationPanel";
import { CaseMessages, AuditTrail } from "@/components/CaseMessages";
import { CaseTimeline } from "@/components/CaseTimeline";
import { InterimApplications } from "@/components/InterimApplications";
import { StructuredPleadings } from "@/components/StructuredPleadings";
import { ConfidentialityGate, ConfidentialityStatus } from "@/components/ConfidentialityGate";
import { CaseAssociates } from "@/components/CaseAssociates";
import { ChangeAdvocate } from "@/components/ChangeAdvocate";
import { CounselChangeOrders } from "@/components/CounselChangeOrders";
import { OrdersProceedings } from "@/components/OrdersProceedings";
import { CaseSummary } from "@/components/CaseSummary";
import { AwardWorkspace, Chronology } from "@/components/AwardWorkspace";
import { TaxInvoices } from "@/components/TaxInvoices";

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const isArb = role === "arbitrator";
  const isAdv = role === "advocate";
  const isAssoc = role === "associate";

  const [kase, setKase] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [procs, setProcs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [adjs, setAdjs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [interim, setInterim] = useState([]);
  const [pleadings, setPleadings] = useState([]);
  const [payEnabled, setPayEnabled] = useState(false);
  const [payPrompt, setPayPrompt] = useState(new URLSearchParams(window.location.search).get("pay") === "1");

  useEffect(() => {
    api.get("/payments/status").then(({ data }) => setPayEnabled(data.razorpay_enabled)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const c = await api.get(`/cases/${id}`);
      setKase(c.data);
      const [p, d, o, a, i, ia, pl] = await Promise.all([
        api.get("/proceedings", { params: { case_id: id } }).catch(() => ({ data: [] })),
        api.get("/documents", { params: { case_id: id } }).catch(() => ({ data: [] })),
        api.get("/orders", { params: { case_id: id } }).catch(() => ({ data: [] })),
        api.get("/adjournments", { params: { case_id: id } }).catch(() => ({ data: [] })),
        api.get("/invoices", { params: { case_id: id } }),
        api.get(`/cases/${id}/interim`).catch(() => ({ data: [] })),
        api.get(`/cases/${id}/pleadings`).catch(() => ({ data: [] })),
      ]);
      setProcs(p.data); setDocs(d.data);
      setOrders(o.data); setAdjs(a.data); setInvoices(i.data);
      setInterim(ia.data); setPleadings(pl.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  if (!kase) return <div className="h-40 border border-border bg-muted animate-pulse" data-testid="case-detail-loading" />;

  const mineHalf = (kase.halves || []).find((h) => h.side === kase.my_side);
  const gated = (content) => (
    <ConfidentialityGate caseId={id} accepted={kase.confidentiality_accepted} onAccepted={load}>{content}</ConfidentialityGate>
  );

  return (
    <div data-testid="case-detail-page">
      <button data-testid="back-to-registry" onClick={() => navigate("/cases")}
        className="flex items-center gap-2 label-caps hover:text-foreground transition-colors duration-200">
        <ArrowLeft className="h-3.5 w-3.5" /> My cases
      </button>

      <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
        <div>
          <p className="font-mono text-xs text-muted-foreground" data-testid="case-file-number">
            {kase.case_number ? `${kase.case_number} · ${kase.ref_id}` : kase.ref_id}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2" data-testid="case-detail-title">{kase.title}</h1>
          <p className="text-sm text-muted-foreground mt-3">
            {kase.claimant} <span className="mx-2 text-blue-900 dark:text-blue-400">versus</span> {kase.respondent}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Badge className={verifyClass(kase.verification_status)} testId="case-verification-status">{kase.verification_status}</Badge>
          <Badge className={statusClass(kase.status)} testId="case-detail-status">{statusLabel(kase.status)}</Badge>
          {isArb && (
            <Select value={kase.status} onValueChange={(v) => act(() => api.put(`/cases/${id}`, { status: v }), "Stage updated")}>
              <SelectTrigger className="rounded-none w-48" data-testid="case-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {isArb && kase.verification_status === "pending" && (
        <VerifyPanel caseId={id} proposed={kase.case_number} onDone={load} />
      )}

      <PlanBanner kase={kase} role={role} onDone={load} payEnabled={payEnabled} />

      {payPrompt && !kase.my_side_paid && mineHalf && kase.can_pay && (
        <div className="mt-6 border-2 border-blue-900 bg-blue-50 dark:bg-blue-950/40 p-6" data-testid="pay-prompt">
          <p className="label-caps">Welcome — one step left</p>
          <p className="font-display text-2xl mt-2">Pay your share of {money(mineHalf.amount)}</p>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
            You are now on record in this matter. Settle {mineHalf.invoice_number} to open the case
            record for your side.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <PayFeeButton invoice={{ id: mineHalf.invoice_id, amount: mineHalf.amount, currency: "INR" }}
              enabled={payEnabled} onPaid={load} />
            <button data-testid="pay-prompt-dismiss" onClick={() => setPayPrompt(false)}
              className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
              Later
            </button>
          </div>
        </div>
      )}

      {!kase.confidentiality_accepted && role !== "owner" && (
        <div className="mt-6 border border-blue-900 bg-blue-50 dark:bg-blue-950/40 p-4 text-sm flex items-center gap-3" data-testid="confidentiality-banner">
          <ShieldCheck className="h-4 w-4 text-blue-900 dark:text-blue-400 shrink-0" />
          <p>The confidentiality agreement for this reference awaits your digital acceptance. Pleadings, documents, orders and awards open once it is signed — see the Documents tab.</p>
        </div>
      )}

      <Tabs defaultValue="file" className="mt-10">
        <div className="mb-6"><CaseTimeline steps={kase.timeline} /></div>
        <TabsList className="rounded-none flex-wrap h-auto" data-testid="case-tabs">
          {[["file", "Case file"], ["pleadings", "Pleadings (SoC / SoD)"], ["orders", "Orders & Proceedings"],
            ["interim", "Interim (S.17)"], ["documents", "Documents"],
            ["adjournments", "Adjournments"],
            ...(!isArb && !isAdv && !isAssoc ? [] : [["messages", "Messages"]]),
            ...(isArb ? [["audit", "Audit trail"]] : []),
            ["fees", isArb ? "My fees" : "Fees"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none text-xs uppercase tracking-widest" data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="file" className="mt-6 space-y-6">
          {kase.archived && (
            <div className="border border-slate-400 bg-slate-100 dark:bg-slate-800 p-4 text-sm" data-testid="archived-banner">
              <p className="label-caps">Archived · read-only</p>
              <p className="mt-1">This reference was archived after the final award and the 90-day challenge window. The record can be read; a complete download of the archive is INR 500 per side.</p>
            </div>
          )}
          {kase.arbitrator_count > 1 && (
            <div className={`border p-4 text-sm ${kase.tribunal_constituted ? "border-green-600 bg-green-50 dark:bg-green-950/30" : "border-amber-400 bg-amber-50 dark:bg-amber-950/30"}`} data-testid="tribunal-status">
              <p className="label-caps">{kase.tribunal_constituted ? "Tribunal constituted" : "Tribunal not yet constituted"}</p>
              <p className="mt-1">{kase.accepted_arbitrator_ids.length} of {kase.arbitrator_count} arbitrator(s) have accepted{kase.presiding ? ` · presiding: ${kase.presiding.name}` : ""}. Any member may upload signed orders and awards.</p>
            </div>
          )}
          {kase.counter_claim && (
            <div className="border border-border bg-card p-4 text-sm flex flex-wrap justify-between gap-3" data-testid="counter-claim-summary">
              <div><p className="label-caps">Counter-claim on record</p><p className="mt-1">{kase.counter_claim.relief_sought}</p></div>
              <p className="font-display text-xl">{money(kase.counter_claim.amount, kase.currency)}</p>
            </div>
          )}
          {isArb && <CaseSummary caseId={id} onOrderRecorded={load} />}
          {(isArb || isAdv || isAssoc) && <Chronology caseId={id} isArb={isArb} />}
          <LimitationPanel kase={kase} caseId={id} isArb={isArb} isAdv={isAdv} invoices={invoices} onReload={load} />
          {isArb && <ConfidentialityStatus caseId={id} />}
          <Section title="Particulars" testId="case-particulars">
            <Row label="Seat / venue" value={kase.seat} />
            <Row label="Claim amount" value={money(kase.claim_amount, kase.currency)} />
            <Row label="Filed on" value={fmtDate(kase.created_at)} />
            <Row label="Brief facts" value={kase.description} />
            {kase.verification_remarks && <Row label="Tribunal remarks" value={kase.verification_remarks} />}
          </Section>

          <Section title="Appearance & contacts" testId="case-contacts">
            {role === "client" ? (
              <Row label="Tribunal" value={`Confidential — ${kase.seats_filled} of ${kase.arbitrator_count} member(s) on record`} />
            ) : (
              <Row label={`Tribunal (${kase.seats_filled} of ${kase.arbitrator_count})`}
                value={kase.arbitrators.map((a) =>
                  `${a.name}${kase.presiding?.id === a.id ? " (presiding)" : ""} (${a.phone || a.email})`).join(", ")} />
            )}
            <Row label="Claimant's counsel"
              value={kase.claimant_advocate ? `${kase.claimant_advocate.name} · ${kase.claimant_advocate.phone || ""} · ${kase.claimant_advocate.email}` : "—"} />
            <Row label="Respondent's counsel"
              value={kase.respondent_advocate ? `${kase.respondent_advocate.name} · ${kase.respondent_advocate.phone || ""} · ${kase.respondent_advocate.email}` : "Not yet on record"} />
            {role !== "client" && (
              <Row label="Clients tagged" value={kase.clients.map((c) => `${c.name} (${c.email})`).join(", ")} />
            )}
            {role !== "client" && kase.associates?.length > 0 && (
              <Row label="Associates" value={kase.associates.map((a) => `${a.name} (${a.email}, ${a.side}'s side)`).join(", ")} />
            )}
          </Section>

          {isAdv && <CaseAssociates kase={kase} caseId={id} user={user} onDone={load} />}
          {role === "client" && kase.my_side && <ChangeAdvocate kase={kase} caseId={id} onDone={load} />}

          {isAdv && (
            <InvitePanel kase={kase} caseId={id} onDone={load} />
          )}

          {isAdv && (
            <Section title="Tagging" testId="tagging-section">
              <div className="px-6 py-5 grid grid-cols-1 lg:grid-cols-2 gap-8">
                <TagForm label="Tag respondent's counsel by email" cta="Tag counsel" testId="tag-advocate"
                  onSubmit={(email) => act(() => api.post(`/cases/${id}/tag-respondent-advocate`, { email }), "Counsel tagged")} />
                <TagForm label={`Tag your client by email (${kase.my_side || "claimant"}'s side)`} cta="Tag client" testId="tag-client"
                  onSubmit={(email) => act(() => api.post(`/cases/${id}/tag-client`, { email, side: kase.my_side || "claimant" }), "Client tagged")} />
              </div>
            </Section>
          )}
        </TabsContent>

        <TabsContent value="pleadings" className="mt-6">
          {gated(<StructuredPleadings caseId={id} items={pleadings} kase={kase} isArb={isArb} mySide={kase.my_side} onReload={load} />)}
        </TabsContent>

        <TabsContent value="interim" className="mt-6">
          {gated(<InterimApplications caseId={id} items={interim} kase={kase} isArb={isArb} mySide={kase.my_side} onReload={load} />)}
        </TabsContent>

        <TabsContent value="messages" className="mt-6"><CaseMessages caseId={id} /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditTrail caseId={id} /></TabsContent>

        <TabsContent value="documents" className="mt-6">{gated(<>
          {(kase.downloads_allowed || kase.archived) && !isAssoc && docs.length > 0 && (
            <a href={`${API}/cases/${id}/documents.zip`} data-testid="bulk-download-button"
              className="inline-flex items-center h-10 px-4 mb-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
              <Download className="h-3.5 w-3.5 mr-2" /> Download all case files ({docs.length}){kase.archived && !isArb ? " · INR 500" : ""}
            </a>
          )}
          <Section title="Pleadings & documents" testId="documents-section"
            form={kase.efiling_allowed ? <DocForm caseId={id} onDone={load} isArb={isArb} /> : (
              <p className="text-xs text-muted-foreground" data-testid="efiling-locked">
                {!kase.my_side_paid
                  ? "E-filing opens as soon as your side's half of the case fee is paid."
                  : "E-filing of pleadings and documents is available on full access. Upgrade this case to file and download documents."}
              </p>
            )}>
            {docs.length === 0 ? (
              <p className="px-6 py-10 text-sm text-muted-foreground">No documents uploaded.</p>
            ) : docs.map((d) => (
              <div key={d.id} className="px-6 py-4 border-b border-border last:border-0 flex items-center justify-between gap-4" data-testid={`document-row-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
                    {d.category} · {d.filename} · {Math.round((d.size || 0) / 1024)} KB · {fmtDate(d.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {kase.downloads_allowed ? (
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                      data-testid={`download-document-${d.id}`}
                      className="text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                      <Download className="h-4 w-4" />
                    </a>
                  ) : isAssoc && kase.my_side_paid ? (
                    <a href={`${API}/documents/${d.id}/view`} target="_blank" rel="noreferrer"
                      data-testid={`view-document-${d.id}`}
                      className="text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200" aria-label="View on screen">
                      <Eye className="h-4 w-4" />
                    </a>
                  ) : (
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground" data-testid={`document-locked-${d.id}`}>
                      {kase.my_side_paid ? "Upgrade to download" : "Locked until your half is paid"}
                    </span>
                  )}
                  {(isArb || isAdv) && (                    <button data-testid={`delete-document-${d.id}`}
                      onClick={() => act(() => api.delete(`/documents/${d.id}`), "Document deleted")}
                      className="text-red-800 hover:text-red-600 transition-colors duration-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </Section>
        </>)}</TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-6">{gated(<>
          {isArb && <CounselChangeOrders caseId={id} orders={orders} onDraft={setPrefill} />}
          {isArb && !["award_passed", "archived"].includes(kase.status) && <AwardWorkspace caseId={id} onCompiled={setPrefill} />}
          <OrdersProceedings kase={kase} caseId={id} orders={orders} procs={procs} isArb={isArb} isAdv={isAdv} isAssoc={isAssoc} onReload={load} prefill={prefill} />
        </>)}</TabsContent>

        <TabsContent value="adjournments" className="mt-6">
          <Section title="Adjournment requests" testId="adjournments-section">
            <p className="px-6 py-4 text-xs text-muted-foreground border-b border-border">
              A request may be raised by either counsel only when advocates for both parties are on record, and at
              least 4 days before the scheduled date.
            </p>
            {adjs.length === 0 ? (
              <p className="px-6 py-10 text-sm text-muted-foreground">No adjournment sought on this reference.</p>
            ) : adjs.map((a) => (
              <div key={a.id} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`adjournment-row-${a.id}`}>
                <div>
                  <p className="text-sm font-medium">{a.reason}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    By {a.requested_by_name} · sitting {fmtDateTime(a.scheduled_at)}
                    {a.preferred_date ? ` · preferred ${fmtDate(a.preferred_date)}` : ""}
                  </p>
                  {a.remarks && <p className="text-xs text-muted-foreground mt-1">Tribunal: {a.remarks}</p>}
                  {a.status !== "pending" && (
                    <div className="mt-3">
                      <SignedPdf kind="adjournment" targetId={a.id}
                        documentId={kase.downloads_allowed ? a.order_document_id : null}
                        canUpload={isArb} onDone={load} testId={`adjournment-pdf-${a.id}`} label="signed adjournment order" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={a.status === "allowed" ? "bg-green-50 text-green-900" : a.status === "rejected" ? "bg-red-50 text-red-900" : "bg-amber-50 text-amber-900"}>{a.status}</Badge>
                  {isArb && a.status === "pending" && (
                    <>
                      <button data-testid={`allow-adjournment-${a.id}`}
                        onClick={() => act(() => api.post(`/adjournments/${a.id}/decide`, { action: "allow", remarks: "Allowed" }), "Adjournment allowed")}
                        className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400">Allow</button>
                      <button data-testid={`reject-adjournment-${a.id}`}
                        onClick={() => act(() => api.post(`/adjournments/${a.id}/decide`, { action: "reject", remarks: "Rejected" }), "Adjournment rejected")}
                        className="text-xs uppercase tracking-wider text-red-800">Reject</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </Section>
        </TabsContent>

        {role !== "client" ? (
          <TabsContent value="fees" className="mt-6">
            <Section title={isArb ? "My fee notes" : "Fee notes"} testId="invoices-section"
              form={isArb && <InvoiceForm onSubmit={(p) => act(() => api.post("/invoices", { ...p, case_id: id }), "Fee note raised")} />}>
              {invoices.length === 0 ? (
                <p className="px-6 py-10 text-sm text-muted-foreground">No fee notes raised.</p>
              ) : invoices.map((i) => (
                <div key={i.id} className="px-6 py-4 border-b border-border last:border-0 flex items-center justify-between gap-4" data-testid={`invoice-row-${i.id}`}>
                  <div>
                    <p className="text-sm font-mono font-medium">{i.invoice_number}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {i.kind === "subscription"
                        ? `Case fee — ${i.side === "respondent" ? "respondent's" : "claimant's"} half`
                        : i.payable_to} · {i.particulars || "Fees"} · due {fmtDate(i.due_on)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm">{money(i.amount, i.currency)}</span>
                    <Badge className={i.status === "paid" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}>{i.status}</Badge>
                    {i.status === "paid" ? (
                      <a href={`${API}/invoices/${i.id}/receipt`} target="_blank" rel="noreferrer"
                        data-testid={`receipt-${i.id}`}
                        className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                        Receipt
                      </a>
                    ) : isAdv && i.kind !== "arbitrator_fee" && i.side === kase.my_side ? (
                      <PayFeeButton invoice={i} enabled={payEnabled} onPaid={load} />
                    ) : isAssoc ? (
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground" data-testid={`assoc-no-pay-${i.id}`}>Payable by the party or counsel</span>
                    ) : isArb ? (
                      <button data-testid={`mark-paid-${i.id}`}
                        onClick={() => window.confirm(`Confirm that ${i.invoice_number} has been received offline and verified?`) &&
                          act(() => api.put(`/invoices/${i.id}`, { status: "paid" }), "Marked paid (offline, verified)")}
                        className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400">Mark paid (verified)</button>
                    ) : (
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground" data-testid={`awaiting-payment-${i.id}`}>Awaiting payment</span>
                    )}
                  </div>
                </div>
              ))}
            </Section>
          </TabsContent>
        ) : (
          <TabsContent value="fees" className="mt-6 space-y-6">
            {!isArb && <TaxInvoices caseId={id} />}
            <Section title="Arbitrator's fees" testId="invoices-section">
              {invoices.length === 0 ? (
                <p className="px-6 py-10 text-sm text-muted-foreground">No fee notes raised.</p>
              ) : invoices.map((i) => (
                <div key={i.id} className="px-6 py-4 border-b border-border last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`invoice-row-${i.id}`}>
                  <div>
                    <p className="text-sm font-mono font-medium">{i.invoice_number}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {i.kind === "subscription"
                        ? `Case fee — ${i.side === "respondent" ? "respondent's" : "claimant's"} half`
                        : i.particulars || "Fees"} · due {fmtDate(i.due_on)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm">{money(i.amount, i.currency)}</span>
                    <Badge className={i.status === "paid" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}>{i.status}</Badge>
                    {i.status !== "paid" ? <PayFeeButton invoice={i} enabled={payEnabled} onPaid={load} /> : (
                      <a href={`${API}/invoices/${i.id}/receipt`} target="_blank" rel="noreferrer"
                        data-testid={`receipt-${i.id}`}
                        className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                        Receipt
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </Section>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

